<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <Header
      :title="isFromAutocomplete ? categoryName || widgetTitle || t('header.default_title', 'Help') : sectionTitle"
      :back-callback="backCallback"
      :track-event-context="CMP_CONTEXTS.ARTICLE"
      hide-action
      hide-language-selector
    />
    <ArticleAutocomplete :track-event-context="CMP_CONTEXTS.ARTICLE" :track-event-selection="articleId" />
    <div ref="container" v-loading.center="{ isLoading: loading }" class="article-container">
      <div class="article-content">
        <div class="article-wrapper">
          <div v-if="showRetry">
            <ThemeButton outlined class="p-2" @click="onRetryClick"> Retry </ThemeButton>
          </div>
          <div class="article-title">
            {{ articleTitle }}
          </div>
          <div class="article-content" v-html="articleBody" />

          <div class="article-rate">
            <div
              v-for="option in ratingOptions"
              :key="option.value"
              class="rate-option"
              :class="{ selected: articleRating === option.value }"
              @click="onArticleRate(option.value)"
            >
              <component :is="articleRating === option.value ? option.filledIcon : option.icon" /><span>{{
                option.text
              }}</span>
            </div>
          </div>

          <!-- Action is show only iff, the connect to support option is not shown in the bottom -->

          <div v-if="canShowAction.box" class="article-actions mt-1">
            <div v-if="canShowAction.warning" class="align-center article-check-container d-flex justify-center p-2">
              <MdInfo class="check-icon mr-1" />
              <span>
                {{ articleCheck.warningMessage }}
              </span>
            </div>
            <ThemeButton
              v-if="canShowAction.button"
              full-width
              class="action-button align-center d-flex justify-center mt-1 p-2"
              @click="goToArticleAction"
            >
              <span>{{ articleMetadata.articleAction.actionTitle }}</span>
              <FaAngleRight class="ml-1" />
            </ThemeButton>
          </div>
        </div>

        <RelatedArticles :article-id="articleId" />
      </div>
      <ArticleFeedback
        :article-action="canShowAction.box && !canShowAction.warning && articleMetadata.articleAction"
        :can-connect-to-support="articleMetadata.canConnectToSupport"
        :enable-metadata-rules="enableMetadataRules"
        @back="onBackClick"
        @connect="goToConnectSupport"
        @action="goToArticleAction"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Header from '@/components/Header.vue';
import {
  ArticleInteractionBus,
  emitArticleAutocompleteFocused,
  emitOpenArticle,
  emitOpenMyRequest,
} from '@/common/eventBus';
import { showRequestLimitErrorModal } from '@/common/dialogs/limitErrorDialog';
import loading from '@/components/loading';
import * as PleasedApi from '@/common/api/api.pleased';
import { ArticleDetail, ArticleMetrics, ArticleRate, BrandConfig, MetadataRule } from '@/common/api/models.pleased';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';
import ThemeButton from './ThemeButton.vue';
import ArticleFeedback from './ArticleFeedback.vue';
import RelatedArticles from './RelatedArticles.vue';
import ArticleAutocomplete from './ArticleAutocomplete.vue';
import LikeIcon from '@/components/icons/LikeIcon.vue';
import DislikeIcon from '@/components/icons/DislikeIcon.vue';

import FilledLikeIcon from './icons/FilledLikeIcon.vue';
import FilledDislikeIcon from './icons/FilledDislikeIcon.vue';

import { namespace } from 'vuex-class';
import {
  buildArticleOption,
  buildConnectConfig,
  buildConnectToAgentWithArticleConfig,
  canConnectToAgent,
  randomId,
  checkABTestingPassed,
  checkAiABTestingPassed,
} from '@/common/bot/lib';
import { ArticleAction, ArticleInteractionExternEvent, ConnectToAgent } from '@/common/bot/types';
import FaAngleRight from './icons/FaAngleRight.vue';
import MdInfo from './icons/MdInfo.vue';
import { Bridge, platform } from '@/common/bridge';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import { CHECKS } from '@/common/bot/flow-frameworks/ChatMessageChecks.static';
import {
  checkActionSupported,
  resolveArticleAction,
  PLEASED_ARTICLE_ACTIONS,
} from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import {
  ChatTriggerActionType,
  ChatTriggerMatchSource,
  manualTriggerCheck,
  MatchedChatTrigger,
} from '@/common/bot/flow-frameworks/chat-trigger';
import { GA_EVENT_CUSTOM_DIMENSION_ENUM, GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import { PleasedUserInfo } from '@/common/types';
import OpenRequestModal from '@/views/Chatroom/OpenRequestModal.vue';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import { analytics, syncLogs, BTN_EVTS, CMP_CONTEXTS } from '@/common/logs';
import { isUserEligibleForAiFlow } from '@/common/bot/flow-frameworks/ai-service/AiService.utils';

const userModule = namespace('user');
const articleAutocompleteModule = namespace('articleAutocomplete');
const articleMetricsModule = namespace('articleMetrics');

@Component({
  components: {
    Header,
    ThemeButton,
    ArticleFeedback,
    ArticleAutocomplete,
    RelatedArticles,
    LikeIcon,
    DislikeIcon,
    FilledLikeIcon,
    FilledDislikeIcon,
    FaAngleRight,
    MdInfo,
    OpenRequestModal,
  },
  directives: {
    loading,
  },
})
export default class Article extends mixins(LogPageEventMixin) {
  @Prop({ default: '' }) readonly articleId!: string;
  @Prop({ default: '' }) readonly title!: string;
  @Prop({ default: '' }) readonly categoryName!: string;
  @Prop({ default: '' }) readonly body!: string;
  @Prop({ default: '' }) readonly sectionTitle!: string;
  @Prop(Boolean) readonly fetchArticle!: boolean;
  @Prop(Boolean) readonly isFromAutocomplete!: boolean;
  @Prop() readonly sourceMessageId!: string;
  @Prop() readonly useConfig!: ConnectToAgent;

  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;
  @userModule.State('isLogin') isLogin!: boolean;
  @userModule.State('userCountry') userCountry!: string;

  @articleAutocompleteModule.State('widgetTitle') widgetTitle!: string;
  @articleMetricsModule.Mutation('setArticleMetrics') setArticleMetrics!: (metrics: ArticleMetrics) => void;
  @articleMetricsModule.Action('saveArticleMetrics') saveArticleMetrics!: (params: {
    endChat?: boolean;
    restartChat?: boolean;
  }) => void;

  readonly GA_EVENT_NAME_ENUM = GA_EVENT_NAME_ENUM;
  readonly GA_EVENT_CUSTOM_DIMENSION_ENUM = GA_EVENT_CUSTOM_DIMENSION_ENUM;
  readonly CMP_CONTEXTS = CMP_CONTEXTS;

  declare $refs: Vue['$refs'] & { container: HTMLDivElement };

  loading = false;
  loadingArticleConfig = false;
  articleParentConnectConfig: ConnectToAgent | null = null;
  showRetry = false;
  fetchedArticle: ArticleDetail | null = null;
  articleRating: ArticleRate | null = null;
  metadataRules: MetadataRule[] = [];
  brandConfig: BrandConfig | null = null;

  articleCheck = {
    showWarning: false,
    warningMessage: '',
    action: null as ArticleAction | null | undefined,
    applied: false,
  };

  articleStartReadTs = Date.now();

  cleanUps: Function[] = [];

  get ratingOptions() {
    return [
      {
        value: ArticleRate.HELPFUL,
        text: this.$t('page.article.feedback.helpful'),
        icon: 'LikeIcon',
        filledIcon: 'FilledLikeIcon',
      },
      {
        value: ArticleRate.NOT_HELPFUL,
        text: this.$t('page.article.feedback.not_helpful'),
        icon: 'DislikeIcon',
        filledIcon: 'FilledDislikeIcon',
      },
    ];
  }

  get articleBody() {
    return this.fetchedArticle?.body || this.body;
  }

  get articleTitle() {
    return this.fetchedArticle?.title || this.title;
  }

  get isKycAction() {
    return this.fetchedArticle?.articleConfig.action === PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION;
  }

  get canShowAction() {
    const showWarning = !!this.articleCheck?.showWarning;

    if (!this.articleMetadata.articleAction) return { box: false, button: false, warning: false };

    const isRegisterAction = this.fetchedArticle?.articleConfig.action === PLEASED_ARTICLE_ACTIONS.GO_REGISTER;

    // Do not show warning/action if KYC & not logged in.
    const showBox = this.isKycAction ? this.isLogin : isRegisterAction ? !this.isLogin : true;
    // Do not show action if KYC & warning is not shown.
    const showButton = this.isKycAction ? showWarning : true;

    if (this.articleMetadata.canConnectToSupport) return { box: showBox, button: showButton, warning: showWarning };
    return { box: showBox, button: false, warning: showWarning };
  }

  get [PAGE_TRACKING]() {
    return {
      name: PAGE_EVENTS.ARTICLE,
      selection: this.articleId,
    };
  }

  created() {
    const backListener = () => {
      emitOpenArticle({
        isOpen: false,
      });
      window.removeEventListener('popstate', backListener);
      this.emitArticleInteraction('back');
    };

    // Fetch only on created to avoid refetching on article change
    this.fetchBrandConfig();

    window.addEventListener('popstate', backListener);
    this.cleanUps.push(() => window.removeEventListener('popstate', backListener));
  }

  destroyed() {
    const container = document.getElementById('messageListContainer');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
    this.setAndSaveArticleMetrics();
    this.cleanUps.map((x) => x());
  }

  get isAiFlowEnabled() {
    if (!this.brandConfig) return false;

    const {
      aiEnabled,
      openAiDown = false,
      abTestPercentage,
      aiAbTestingEnabled,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,
    } = this.brandConfig;

    return isUserEligibleForAiFlow({
      openAiDown,
      aiEnabledPerBrand: aiEnabled,
      aiEnabledPerSession: true,
      aiAbTestingEnabled,
      abTestPercentage,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,

      isUserLoggedIn: !!this.isLogin,
      userId: this.pleasedUser.userId,
      userLoyaltyTier: this.pleasedUser.userLoyaltyTierBand,
      skipAiChat: this.builtArticleConnectConfig?.skipAiChat,
      enableMetadataRules: this.builtArticleConnectConfig?.enableMetadataRules,
    });
  }

  get builtArticleConnectConfig() {
    if (this.loading || !this.fetchedArticle || !this.articleParentConnectConfig) {
      return null;
    }

    const { id, title, articleConfig } = this.fetchedArticle;

    const articleOption = buildArticleOption({
      ...articleConfig,
      id,
      title,
      category: this.articleCategoryName,
    });

    return buildConnectToAgentWithArticleConfig(this.articleParentConnectConfig, articleOption);
  }

  get connectConfig(): ConnectToAgent | null {
    if (!this.builtArticleConnectConfig) return null;

    const extraFields = {
      abTestingPassed: this.abTestingPassed,
    };

    return {
      ...this.builtArticleConnectConfig,
      ...extraFields,
    };
  }

  get enableMetadataRules() {
    return this.connectConfig?.enableMetadataRules && this.metadataRules.length > 0;
  }

  get abTestingPassed() {
    if (this.loading) return false;

    return checkABTestingPassed({
      userId: this.pleasedUser.userId,
      abValue: this.fetchedArticle?.articleConfig.abValue || '',
      enableAbTesting: this.fetchedArticle?.articleConfig.enableAbTesting || false,
      articleId: this.articleId,
    });
  }

  get articleMetadata() {
    if (this.loading || !this.fetchedArticle || !this.articleParentConnectConfig) {
      return {
        articleAction: null,
        canConnectToSupport: false,
      };
    }

    // We can connect user to AI or Agent
    const canConnectToSupport = this.isAiFlowEnabled || canConnectToAgent(this.connectConfig);

    const articleAction =
      Boolean(this.fetchedArticle?.articleConfig.action) &&
      checkActionSupported({
        action: this.fetchedArticle!.articleConfig.action,
      }) &&
      resolveArticleAction({
        action: this.fetchedArticle.articleConfig.action,
        actionMessage: this.fetchedArticle.articleConfig.actionMessage,
        actionTitle: this.fetchedArticle.articleConfig.actionText,
      } as ArticleAction);

    return {
      // Override the action based on the article check.
      articleAction: this.articleCheck.applied ? this.articleCheck.action : articleAction,
      canConnectToSupport,
    };
  }

  get articleGAParams() {
    return {
      event_category: 'Article',
      [GA_EVENT_CUSTOM_DIMENSION_ENUM.CONTACT_SUPPORT_AVAILABLE]: this.articleMetadata.canConnectToSupport,
      [GA_EVENT_CUSTOM_DIMENSION_ENUM.ARTICLE_ACTION_AVAILABLE]: Boolean(this.articleMetadata.articleAction),
      [GA_EVENT_CUSTOM_DIMENSION_ENUM.ARTICLE_OPEN_SOURCE]: this.isFromAutocomplete ? 'search' : 'chatroom',
    };
  }

  get articleCategoryName() {
    return this.fetchedArticle?.category?.titleTranslated || this.fetchedArticle?.category?.title || '';
  }

  goToAction(action: ArticleAction) {
    Bridge.goToAction(action.action);
  }

  setAndSaveArticleMetrics() {
    const duration = Math.floor((Date.now() - this.articleStartReadTs) / 1000);

    this.setArticleMetrics({
      articleId: this.articleId,
      connectToAgent: this.articleMetadata.canConnectToSupport,
      abValue: this.fetchedArticle!.articleConfig.abValue || '',
      duration,
    });

    this.saveArticleMetrics({});
  }

  @Watch('articleId')
  setArticleMetricsOnIdChange() {
    this.setAndSaveArticleMetrics();
  }

  @Watch('articleId', { immediate: true })
  onArticleIdChange() {
    if (this.fetchArticle) {
      this.fetchArticleDetails().then(() => {
        this.runCheck();
      });
    }

    this.fetchArticleRating();
    this.fetchArticleParentConfig();
    this.fetchMetadataRules();
    this.checkArticleTrigger();
    this.$refs.container && this.$refs.container.scrollTo(0, 0);
  }

  onRetryClick() {
    this.showRetry = false;
    this.fetchArticleDetails();
  }

  onArticleRate(rate: ArticleRate) {
    const gaEvent =
      rate === ArticleRate.HELPFUL ? GA_EVENT_NAME_ENUM.ARTICLE_HELPFUL : GA_EVENT_NAME_ENUM.ARTICLE_NOT_HELPFUL;
    this.$gtag.event(gaEvent, this.articleGAParams);

    const eventName = rate === ArticleRate.HELPFUL ? BTN_EVTS.ARTICLE_HELPFUL : BTN_EVTS.ARTICLE_NOT_HELPFUL;
    analytics.trackClick({
      name: eventName,
      selection: this.articleId,
    });

    this.articleRating = rate;
    PleasedApi.rateArticle({
      articleId: +this.articleId,
      rating: rate,
    });
  }

  backCallback() {
    this.$gtag.event(this.GA_EVENT_NAME_ENUM.ARTICLE_GO_BACK, this.articleGAParams);
    setTimeout(() => {
      emitArticleAutocompleteFocused(true);
    }, 100);
    history.back(); // To trigger popState listener
  }

  onBackClick() {
    analytics.trackClick({
      name: BTN_EVTS.ARTICLE_INTERACT_BACK,
      selection: this.articleId,
    });

    this.backCallback();
  }

  goToConnectSupport() {
    this.$gtag.event(this.GA_EVENT_NAME_ENUM.ARTICLE_CONTACT_SUPPORT, this.articleGAParams);
    analytics.trackClick({
      name: BTN_EVTS.ARTICLE_INTERACT_CONTACT_SUPPORT,
      selection: this.articleId,
    });

    this.emitArticleInteraction('support-request');
    emitOpenArticle({ isOpen: false });
  }

  async goToArticleAction() {
    if (!this.articleMetadata.articleAction) return;

    this.$gtag.event(this.GA_EVENT_NAME_ENUM.ARTICLE_ACTION_USAGE, {
      ...this.articleGAParams,
      [this.GA_EVENT_CUSTOM_DIMENSION_ENUM.ARTICLE_ACTION_TYPE]: this.articleMetadata.articleAction.action,
    });

    analytics.trackClick({
      name: BTN_EVTS.ARTICLE_INTERACT_ACTION,
      selection: this.articleId,
    });

    const isAppPlatform = platform === 'ios' || platform === 'app';
    await syncLogs({ endSession: !isAppPlatform });
    Bridge.goToAction(this.articleMetadata.articleAction.action);
  }

  articleInteracted = false;

  emitArticleInteraction(interaction: ArticleInteractionExternEvent['payload']['interaction']) {
    if (this.articleInteracted || !this.connectConfig) return;
    this.articleInteracted = true;

    if (this.loadingArticleConfig || this.loading) {
      // TODO: wait for loading to complete
    }

    const article = this.fetchedArticle!;

    const { action, ...articleOption } = buildArticleOption({
      ...article.articleConfig,
      id: article.id,
      title: article.title,
      category: this.articleCategoryName,
    });

    ArticleInteractionBus.pub({
      id: 'ArticleInteraction-' + randomId(),
      type: ExternalBotEvent.ARTICLE_PAGE_INTERACTION,
      payload: {
        interaction,
        article: {
          ...articleOption,
          ...(action &&
          this.canShowAction.box &&
          (!this.canShowAction.warning || this.isKycAction) &&
          checkActionSupported({
            action: this.fetchedArticle!.articleConfig.action!,
          })
            ? { action: resolveArticleAction(action) }
            : {}),
        },
        supportOptions: this.connectConfig,
        sourceMessageId: this.sourceMessageId,
        connectToAI: this.isAiFlowEnabled,
      },
    });
  }

  async fetchArticleDetails() {
    this.loading = true;

    try {
      const articleDetails = await PleasedApi.getArticleDetails.cached({ articleId: this.articleId });
      this.fetchedArticle = articleDetails.data;
      document.title = `Article | ${this.fetchedArticle.enTitle || this.fetchedArticle.title} | ${this.articleId}`;
      window.history.pushState('forward', '', `article?articleId=${this.articleId}`); // For handling Back button
    } catch (error) {
      captureEventException(error, ErrorTrackingEvent.ArticleLoad);
      this.showRetry = true;
    } finally {
      this.loading = false;
    }
  }

  fetchArticleRating() {
    PleasedApi.getArticleRating({ articleId: +this.articleId }).then((res) => {
      this.articleRating = res.data.rating;
    });
  }

  async fetchMetadataRules() {
    if (!this.isLogin || !this.articleId) return;
    const rules = await PleasedApi.getMetadataAutomations({ articleId: this.articleId });
    this.metadataRules = rules.data;
  }

  checkArticleTrigger() {
    manualTriggerCheck(
      {
        type: ChatTriggerMatchSource.ARTICLE,
        id: this.articleId,
        userCountry: this.userCountry,
        triggerEvent: null,
        triggerMessage: null,
      },
      (matched) => {
        if (matched) this.showOverlayTrigger(matched);
      }
    );
  }

  fetchArticleParentConfig() {
    if (this.useConfig) {
      // If a config is provided, override it.
      this.articleParentConnectConfig = this.useConfig;
      return;
    }
    if (this.loadingArticleConfig) return;

    this.loadingArticleConfig = true;

    PleasedApi.getArticleParentConfig
      .cached({ articleId: this.articleId })
      .then((sectionConfig) => buildConnectConfig({ ...sectionConfig.data }))
      .then((config) => {
        this.articleParentConnectConfig = config;
      })
      .finally(() => {
        this.loadingArticleConfig = false;
      });
  }

  fetchBrandConfig() {
    PleasedApi.getBrandConfig().then((brandConfig) => {
      this.brandConfig = brandConfig.data.brandConfig;
    });
  }

  async runCheck() {
    if (!this.isLogin) return;

    // This is the linked check article.
    const checkActionId = this.fetchedArticle?.articleConfig.checkActionId as number;
    const checks = this.fetchedArticle?.articleConfig.checks;

    const action = this.fetchedArticle?.articleConfig.action;
    const isKYCAction = action === PLEASED_ARTICLE_ACTIONS.GO_KYC_VERIFICATION;

    if (!checkActionId && !checks && !isKYCAction) return;

    // Determine check type, there are 2 types:
    // 1. Run the check configured in the current article.
    // 2. or, run the check in the linked article.

    const checkArticle =
      checks || isKYCAction
        ? this.fetchedArticle
        : (await PleasedApi.getArticleDetails.cached({ articleId: checkActionId })).data;

    if (!checkArticle) return;

    const article = checkArticle;
    const category = checkArticle.category;

    const checkArticleOption = buildArticleOption({
      ...article.articleConfig,
      id: article.id,
      title: article.title,
      category: category?.titleTranslated || category?.title || '',
    });
    // TODO: If more checks are added, then make
    // the checks more dynamic by proper keys.
    await CHECKS.KYC.check().then((checkPassed) => {
      if (checkPassed) {
        this.articleCheck = {
          showWarning: false,
          warningMessage: '',
          action: null,
          applied: true,
        };
      } else {
        const resolvedAction = checkActionSupported(checkArticleOption.action)
          ? resolveArticleAction(checkArticleOption.action!)
          : null;
        const isDepositAction = resolvedAction && resolvedAction.action === PLEASED_ARTICLE_ACTIONS.GO_DEPOSIT;

        this.articleCheck = {
          showWarning: true,
          warningMessage: isDepositAction ? CHECKS.KYC.slots().warning : CHECKS.KYC.slots().commonWarning,
          action: resolvedAction,
          applied: true,
        };
      }
    });
  }

  showTriggerModal(trigger: MatchedChatTrigger) {
    analytics.trackPageEvent({
      name: PAGE_EVENTS.MODAL_TRIGGER_POP_UP,
      selection: trigger.chatTriggerId,
      enter: true,
    });

    const trackPageLeave = () => {
      analytics.trackPageEvent({
        name: PAGE_EVENTS.MODAL_TRIGGER_POP_UP,
        selection: trigger.chatTriggerId,
        enter: false,
      });
    };

    const triggerDialog = this.$dialog({
      content: trigger.message,
      className: 'overlay-trigger',
      buttons: [
        {
          name: this.$t('global.continue'),
          callback: () => {
            analytics.trackClick({
              name: BTN_EVTS.MODAL_TRIGGER_POP_UP_CONTINUE,
              selection: trigger.chatTriggerId,
            });

            trackPageLeave();
            trigger.report.continue();
            triggerDialog.close();
          },
        },
        {
          name: this.$t('global.end_chat'),
          class: 'primary',
          callback: async () => {
            analytics.trackClick({
              name: BTN_EVTS.MODAL_TRIGGER_POP_UP_END_CHAT,
              selection: trigger.chatTriggerId,
            });

            trackPageLeave();

            await syncLogs({
              promises: [trigger.report.end()],
            });

            triggerDialog.close();
            Bridge.sendToParent('CLOSE');
          },
        },
      ],
    });
  }

  showOpenRequestModal(trigger: MatchedChatTrigger) {
    const endChat = async () => {
      await syncLogs({
        promises: [trigger.report.end()],
      });

      triggerDialog.close();
      Bridge.sendToParent('CLOSE');
    };

    const restartChat = () => {
      this.emitArticleInteraction('restart-chat');
      triggerDialog.close();
      emitOpenArticle({ isOpen: false });
    };

    const goToMyRequests = () => {
      this.emitArticleInteraction('go-to-my-requests');
      triggerDialog.close();
      emitOpenArticle({ isOpen: false });
      emitOpenMyRequest(true);
    };

    const selectedOptions = {
      category: this.articleCategoryName,
      section: undefined,
      article: this.articleTitle,
    };

    let triggerDialog: { close(): void; loading(val: boolean): void };

    const showLimitModal = () => {
      triggerDialog.close();

      showRequestLimitErrorModal(this, () => {
        this.emitArticleInteraction('go-to-my-requests');
        emitOpenArticle({ isOpen: false });
        emitOpenMyRequest(true);
      });
    };

    triggerDialog = this.$dialog({
      componentBackground: 'transparent',
      content: this.$createElement(OpenRequestModal, {
        props: {
          trigger,
          selectedOptions,
        },
        on: {
          goToMyRequest: goToMyRequests,
          restartChat,
          endChat,
          showRequestLimitModal: showLimitModal,
        },
      }),
    });
  }

  showOverlayTrigger(trigger: MatchedChatTrigger) {
    const { actionType } = trigger;

    if (actionType.id === ChatTriggerActionType.SHOW_MODAL) {
      this.showTriggerModal(trigger);
    } else if (actionType.id === ChatTriggerActionType.SHOW_OPEN_REQUEST_MODAL) {
      this.showOpenRequestModal(trigger);
    }
  }

  @Watch('loading')
  @Watch('loadingArticleConfig')
  onLoadingChange() {
    // Once article and parent config loaded
    if (!this.loading && !this.loadingArticleConfig && this.fetchedArticle && this.articleParentConnectConfig) {
      this.$gtag.event(this.GA_EVENT_NAME_ENUM.OPEN_ARTICLE, this.articleGAParams);
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/variable.scss';
.article-container {
  height: calc(100vh - #{$header-height} - #{$article-autocomplete-input-block-height});
  background-color: $article-background;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  overflow-y: scroll;
}

.article-wrapper {
  padding: 16px 20px;
  color: $article-content-text-color;

  .article-title {
    @extend .text-bold;
    font-size: $size-m;
    padding: 20px 0;
    color: $article-title-color;
  }
  .article-content {
    @extend .text-regular;
    width: 100%;
    font-size: $size-m2;
    line-height: 2.2rem;

    .ql-size-huge {
      font-size: $size-xl;
    }

    .ql-size-large {
      font-size: $size-l;
    }

    .ql-size-small {
      font-size: $size-xs;
    }

    a {
      color: $article-link-color;
    }

    img {
      max-width: 80%;
    }
  }
}

.article-rate {
  display: flex;
  gap: 20px;
  margin-top: 20px;
  padding: 0;
  color: $article-link-color;

  .rate-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;

    font-weight: 500;
    cursor: pointer;
  }
}

.article-check-container {
  background-color: $article-check-container-background;
  line-height: 1.2;

  font-size: $size-m2;
  .check-icon {
    font-size: $size-xl;
    color: $article-check-container-icon-color;
  }
}
.article-actions {
  .action-button {
    font-size: $size-m;
  }
}
</style>
